import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import {
  getSpecialEvents,
  createSpecialEvent,
  updateSpecialEvent,
  deleteSpecialEvent,
  SpecialEventItem,
} from "../api/specialEventsApi";

interface UseSpecialEventsOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
}

export const useSpecialEvents = ({
  hotelId,
  initialPage = 1,
  initialLimit = 20,
}: UseSpecialEventsOptions) => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();

  const [items, setItems] = useState<SpecialEventItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedItem, setSelectedItem] = useState<SpecialEventItem | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [cache, setCache] = useState<Record<string, SpecialEventItem>>({});

  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  // Cache management
  const updateCache = useCallback((item: SpecialEventItem) => {
    setCache((prev) => ({
      ...prev,
      [item.eventId]: item,
    }));
  }, []);

  const removeFromCache = useCallback((id: string) => {
    setCache((prev) => {
      const newCache = { ...prev };
      delete newCache[id];
      return newCache;
    });
  }, []);

  const {
    data: eventsResponse,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      "special-events",
      hotelId,
      pagination.page,
      pagination.limit,
      searchTerm.trim() || "",
    ],
    // Backend filters by auth context, but keep key tied to hotelId for cache partitioning.
    enabled: true,
    queryFn: async () => {
      const { data, meta } = await getSpecialEvents(
        callApi,
        pagination.page,
        pagination.limit,
        searchTerm
      );

      return {
        items: data,
        total: meta.totalCount || 0,
      };
    },
  });

  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  useEffect(() => {
    setError(queryError instanceof Error ? queryError : null);
  }, [queryError]);

  useEffect(() => {
    if (!eventsResponse) return;
    const { items: events, total } = eventsResponse;
    setItems(events);
    setTotal(total);
    events.forEach(updateCache);
  }, [eventsResponse, setTotal, updateCache]);

  const fetchItems = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, setPage]);

  // Create item
  const createItem = useCallback(
    async (
      item: Omit<SpecialEventItem, "eventId" | "createdAt" | "updatedAt">
    ) => {
      try {
        const createdItem = await createSpecialEvent(callApi, item);
        setItems((prev) => [...prev, createdItem]);
        updateCache(createdItem);
        queryClient.invalidateQueries({ queryKey: ["special-events", hotelId] });
        return createdItem;
      } catch (error) {
        console.error("useSpecialEvents: Error creating item:", error);
        throw error;
      }
    },
    [callApi, updateCache, queryClient, hotelId]
  );

  // Update item
  const updateItem = useCallback(
    async (id: string, updates: Partial<SpecialEventItem>) => {
      try {
        const originalItem = cache[id];
        if (!originalItem) {
          throw new Error("Item not found");
        }

        const result = await updateSpecialEvent(callApi, id, updates);
        setItems((prev) =>
          prev.map((item) => (item.eventId === id ? result : item))
        );
        updateCache(result);
        queryClient.invalidateQueries({ queryKey: ["special-events", hotelId] });
        return result;
      } catch (error) {
        console.error("useSpecialEvents: Error updating item:", error);
        throw error;
      }
    },
    [callApi, cache, updateCache, queryClient, hotelId]
  );

  // Delete item
  const deleteItem = useCallback(
    async (id: string) => {
      try {
        const itemToDelete = cache[id];
        if (!itemToDelete) {
          throw new Error("Item not found");
        }

        await deleteSpecialEvent(callApi, id);

        setItems((prev) => prev.filter((item) => item.eventId !== id));
        removeFromCache(id);
        queryClient.invalidateQueries({ queryKey: ["special-events", hotelId] });
      } catch (error) {
        console.error("useSpecialEvents: Error deleting item:", error);
        throw error;
      }
    },
    [callApi, cache, removeFromCache, queryClient, hotelId]
  );

  // Apply a local optimistic update to a specific event (e.g., for preview images)
  const applyLocalItemUpdate = useCallback(
    (id: string, partial: Partial<SpecialEventItem>) => {
      setItems((prev) =>
        prev.map((it) => (it.eventId === id ? { ...it, ...partial } : it))
      );
      setCache((prev) =>
        prev[id] ? { ...prev, [id]: { ...prev[id], ...partial } } : prev
      );
    },
    []
  );

  return {
    items,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    pagination,
    setPage,
    setLimit,
    refresh: fetchItems,
    setSearchTerm,
    applyLocalItemUpdate,
  };
};
